/**
 * WhalesWidget - Список "китов" (топ-плательщиков)
 * ====================================================
 *
 * Показывает игроков с суммарным доходом (IAP + реклама) выше порога.
 * Платформа/проект/кампании на список не влияют, а глобальный диапазон дат
 * задаёт окно подсчёта дохода. Пустой глобальный диапазон означал бы подсчёт
 * по всей истории — это полный скан таблицы событий плюс 76 млн строк
 * AppLovin (замер: 9.3 s, 22.6 GiB), поэтому виджет подставляет последние
 * 30 полных дней, как сессии и прогрессия.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';

const MIN_REVENUE = 1000;

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function SortableHead({ label, sortKey, sortConfig, onSort, className = '' }) {
  return (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {label} {sortConfig.key === sortKey && (sortConfig.direction === 'asc' ? '↑' : '↓')}
    </TableHead>
  );
}

export function WhalesWidget() {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });

  // Диапазон дат обязателен: без него лидерборд считается по всей истории
  // (9.3 s и 22.6 GiB на вызов), поэтому пустой глобальный диапазон
  // заменяем окном по умолчанию
  const widgetData = useWidgetData(async (filters, options) => {
    return MetricsService.getWhales(MIN_REVENUE, withDefaultDateRange(filters), options);
  });

  const widgetProps = useBaseWidget(widgetData);
  const { data: whales } = widgetProps;

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedWhales = useMemo(() => {
    const list = Array.isArray(whales) ? whales : [];
    const sorted = [...list].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'nickname') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortConfig.key === 'regDate' || sortConfig.key === 'lastActive') {
        aValue = aValue ? new Date(aValue.replace(' ', 'T')).getTime() : 0;
        bValue = bValue ? new Date(bValue.replace(' ', 'T')).getTime() : 0;
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [whales, sortConfig]);

  return (
    <BaseWidget
      {...widgetProps}
      title="Киты"
      subtitle={`Игроки с суммарным доходом более ${MetricsService.formatCurrency(MIN_REVENUE)} за выбранный период${
        sortedWhales.length ? ` · всего: ${sortedWhales.length}` : ''
      }`}
      emptyMessage="Нет игроков с доходом выше порога"
      skeletonRows={6}
    >
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead label="Никнейм" sortKey="nickname" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHead label="Дата регистрации" sortKey="regDate" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHead label="Последняя активность" sortKey="lastActive" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHead label="IAP revenue" sortKey="iapRevenue" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
              <SortableHead label="Ad revenue" sortKey="adRevenue" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
              <SortableHead label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedWhales.map((whale) => (
              <TableRow key={whale.deviceId}>
                <TableCell className="font-medium">{whale.nickname || '—'}</TableCell>
                <TableCell>{formatDate(whale.regDate)}</TableCell>
                <TableCell>{formatDate(whale.lastActive)}</TableCell>
                <TableCell className="text-right">{MetricsService.formatCurrency(whale.iapRevenue)}</TableCell>
                <TableCell className="text-right">{MetricsService.formatCurrency(whale.adRevenue)}</TableCell>
                <TableCell className="text-right font-semibold">{MetricsService.formatCurrency(whale.revenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </BaseWidget>
  );
}

export default WhalesWidget;
